// client/src/components/marketer/MarketerProjectCreate.js
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PROJECT_CREATION_PAUSED, V2_UPDATE_DATE_TEXT, V2_UPDATE_AT } from '../../config/maintenance';
import StepBox from './project/StepBox';
import { TYPES, PLATFORMS, humanSize, sumBytes } from './project/constants';
import ServiceTypeModal from './project/modals/ServiceTypeModal';
import ProductModal from './project/modals/ProductModal';
import ImageModal from './project/modals/ImageModal';
import RecruitModal from './project/modals/RecruitModal';
import RewardModal from './project/modals/RewardModal';
import PerRewardModal from './project/modals/PerRewardModal';
import ScheduleModal from './project/modals/ScheduleModal';
import DescriptionModal from './project/modals/DescriptionModal';
import ManagerModal from './project/modals/ManagerModal';
import ProjectPreviewModal from './project/modals/ProjectPreviewModal';
import ContractModal from './project/modals/ContractModal';
import DoneModal from './project/modals/DoneModal';
import ProcessingModal from './project/modals/ProcessingModal';
import ConfirmModal from './project/modals/ConfirmModal';
import AlertModal from './project/modals/AlertModal';

function plainLen(html) {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return (div.textContent || div.innerText || '').replace(/\u200B/g, '').length;
}

export default function MarketerProjectCreate() {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState('');
  const [serviceType, setServiceType] = useState('');

  const [openServiceModal, setOpenServiceModal] = useState(false);
  const [openProductModal, setOpenProductModal] = useState(false);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [openRecruitModal, setOpenRecruitModal] = useState(false);
  const [openRewardModal, setOpenRewardModal] = useState(false);
  const [openPerRewardModal, setOpenPerRewardModal] = useState(false);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [openDescModal, setOpenDescModal] = useState(false);
  const [openManagerModal, setOpenManagerModal] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [openContract, setOpenContract] = useState(false);
  const [openProcessing, setOpenProcessing] = useState(false);
  const [openDone, setOpenDone] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const [product, setProduct] = useState({ name:'', price:'', url:'', purchasePlace:'', placeName:'', placeAddress:'', customTitle:'', customDesc:'' });
  const [cover, setCover] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [recruit, setRecruit] = useState({ contentType:'', platforms:[] });
  const [reward, setReward] = useState({ rewardPointPerCreator:'', headcount:'' });
  const [perReward, setPerReward] = useState({ use:null, rewardPerBuyer:'', budgetTotal:'', settlementCount:'' });
  const [schedule, setSchedule] = useState({ recruitEnd:'', submitEnd:'', settlementDates:[] });
  const [description, setDescription] = useState(''); // HTML
  const [manager, setManager] = useState({ phone:'', email:'', officeHour:'' });

  const serviceLabel = useMemo(() => TYPES.find(t => t.key === serviceType)?.label || '', [serviceType]);

  const productFilled = useMemo(() => {
    switch (serviceType) {
      case 'delivery': return !!(product.name && product.url && Number(product.price) >= 0);
      case 'purchase': return !!(product.name && product.purchasePlace && Number(product.price) >= 0);
      case 'visit':    return !!(product.placeName && product.placeAddress);
      case 'free':     return !!(product.customTitle && product.customDesc);
      default: return false;
    }
  }, [serviceType, product]);

  const imagesFilled = useMemo(() => !!cover, [cover]);
  const recruitFilled = useMemo(() => ['post','video','free'].includes(recruit.contentType) && (recruit.platforms || []).length > 0, [recruit]);
  const rewardFilled = useMemo(() => {
    const rwd = reward.rewardPointPerCreator;
    const head = reward.headcount;
    const rwdNum = Number(rwd);
    const headNum = Number(head);
    const rewardValid = (
      rwd !== '' && 
      rwd !== null && 
      rwd !== undefined &&
      Number.isFinite(rwdNum) && 
      rwdNum >= 0
    );
    const headValid = Number.isInteger(headNum) && headNum >= 1 && headNum <= 100;
    return rewardValid && headValid;
  }, [reward]);

  const perRewardFilled = useMemo(() => {
    if (perReward.use === null || typeof perReward.use === 'undefined') return false;
    if (!perReward.use) return true;
    const a = Number(perReward.rewardPerBuyer), b = Number(perReward.budgetTotal), c = Number(perReward.settlementCount);
    return Number.isFinite(a) && a >= 0 && Number.isFinite(b) && b >= Math.max(0, a*10) && Number.isInteger(c) && c >= 1 && c <= 4;
  }, [perReward]);

  const scheduleFilled = useMemo(() => {
    if (!schedule.recruitEnd || !schedule.submitEnd) return false;
    if (perReward.use && Number(perReward.settlementCount || 0) > 0) {
      return Array.isArray(schedule.settlementDates) && schedule.settlementDates.length === Number(perReward.settlementCount);
    }
    return true;
  }, [schedule, perReward]);

  const descFilled = useMemo(() => {
    const len = plainLen(description);
    return len > 0 && len <= 2000;
  }, [description]);

  const managerHasAny = useMemo(() => !!(manager.phone || manager.email || manager.officeHour), [manager]);

  const canProceed = (serviceType && productFilled && cover && recruitFilled && rewardFilled && perRewardFilled && scheduleFilled && descFilled);

  const completedSteps = [
    serviceType,
    productFilled,
    imagesFilled,
    recruitFilled,
    rewardFilled,
    perRewardFilled,
    scheduleFilled,
    descFilled,
    managerHasAny
  ].filter(Boolean).length;

  const resetAllAfterServiceChange = () => {
    setProduct({ name:'', price:0, url:'', purchasePlace:'', placeName:'', placeAddress:'', customTitle:'', customDesc:'' });
    setCover(null); setGallery([]);
    setRecruit({ contentType:'', platforms:[] });
    setReward({ rewardPointPerCreator:'', headcount:'' });
    setPerReward({ use:null, rewardPerBuyer:'', budgetTotal:'', settlementCount:'' });
    setSchedule({ recruitEnd:'', submitEnd:'', settlementDates:[] });
    setDescription('');
    setManager({ phone:'', email:'', officeHour:'' });
  };

  const fmt = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  // ── v2 전환: 신규 프로젝트 생성 일시 중단 안내 (10/10 v2 배포 시 이 블록 제거) ──
  // 모든 훅 선언 이후에 조기 반환 (Rules of Hooks). 기존 위저드 코드는 그대로 보존.
  if (PROJECT_CREATION_PAUSED) {
    const datePassed = Date.now() >= V2_UPDATE_AT; // 날짜 경과 후에도 플래그가 남아있는 경우 폴백
    return (
      <div>
        <div className="mb-page-header">
          <h2 className="mb-page-title">인플루언서 모집하기</h2>
          <p className="mb-page-description">{V2_UPDATE_DATE_TEXT} 업데이트까지 신규 프로젝트 생성이 중단됩니다</p>
        </div>
        <div className="mbh-reghero" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
          <div className="mbh-reghero__t" style={{ fontSize: 18 }}>
            🚀 {V2_UPDATE_DATE_TEXT}, 셀프애드가 완전히 새로워집니다
          </div>
          <p style={{ margin: 0, color: '#555', lineHeight: 1.6 }}>
            대규모 업데이트 준비로 <b>신규 프로젝트 생성이 일시 중단</b>되었습니다.<br />
            {datePassed
              ? <>업데이트 작업이 마무리되는 대로 프로젝트 생성이 재개됩니다. 조금만 기다려주세요.</>
              : <>프로젝트 생성은 <b>{V2_UPDATE_DATE_TEXT}부터</b> 가능합니다.</>}<br />
            진행 중인 프로젝트, 보유 포인트, 제출된 결과물과 예정된 결산 일정은
            업데이트 이후에도 <b>그대로 유지</b>됩니다.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="mb-btn mb-btn-secondary"
              onClick={() => navigate('/marketer/mybiz?tab=project&sub=prjOn')}>
              진행중인 프로젝트 보기
            </button>
            <button type="button" className="mb-btn mb-btn-secondary"
              onClick={() => navigate('/inquiry', { state: { role: 'marketer' } })}>
              문의하기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">인플루언서 모집하기</h2>
        <p className="mb-page-description">9단계를 순서대로 완료하여 광고 프로젝트를 생성하세요</p>
      </div>

      {/* 진행 상황 히어로 (A안) */}
      <div className="mbh-reghero">
        <div className="mbh-reghero__num">
          <b>{completedSteps}</b><small> / 9 완료</small>
        </div>
        <div className="mbh-reghero__meta">
          <div className="mbh-reghero__t">
            {completedSteps >= 8
              ? '준비 완료! 이제 프로젝트를 생성할 수 있어요'
              : `${8 - completedSteps}단계만 더 채우면 모집을 시작할 수 있어요`}
          </div>
          <div className="mbh-reghero__bar">
            <div className="mbh-reghero__fill" style={{ width: `${Math.round((completedSteps / 9) * 100)}%` }} />
          </div>
        </div>
      </div>

      {/* 섹션 1: 기본 정보 설정 */}
      <div className="stepgroup">기본 정보 설정</div>
      <StepBox
        title="1. 생성할 광고 프로젝트의 방식을 선택해주세요."
        actionLabel="선택하기"
        onAction={() => {
          if (serviceType) {
            setOpenConfirm(true);
          } else {
            setOpenServiceModal(true);
          }
        }}
        filled={!!serviceType}
        valueText={serviceLabel}
      />

      <StepBox
        title="2. 광고할 제품/장소/서비스에 대한 정보를 입력해주세요."
        actionLabel="입력하기"
        onAction={() => { if (!serviceType) return setAlertMessage('먼저 1단계를 완료해주세요.'); setOpenProductModal(true); }}
        filled={productFilled}
        valueText={
          serviceType === 'delivery'  ? `${product.name || '-'} · ${Number(product.price||0).toLocaleString()} · ${product.url || ''}` :
          serviceType === 'purchase'  ? `${product.name || '-'} · ${Number(product.price||0).toLocaleString()} · ${product.purchasePlace || ''}` :
          serviceType === 'visit'     ? `${product.placeName || '-'} · ${product.placeAddress || ''}` :
          serviceType === 'free'      ? `${product.customTitle || '-'} · ${product.customDesc ? '(입력됨)' : ''}` : ''
        }
      />

      <StepBox
        title="3. 광고할 제품/장소/서비스 사진을 첨부해주세요."
        actionLabel="첨부하기"
        onAction={() => { if (!serviceType || !productFilled) return setAlertMessage('1~2단계를 먼저 완료해주세요.'); setOpenImageModal(true); }}
        filled={imagesFilled}
        valueText={cover ? `대표: ${cover.name}${gallery.length ? ` · 세부: ${gallery.length}장, 총 ${humanSize((cover?.size||0)+sumBytes(gallery))}` : ''}` : ''}
      />

      {/* 섹션 2: 모집 조건 설정 */}
      <div className="stepgroup">모집 조건 설정</div>
      <StepBox
        title="4. 제품/장소/서비스를 광고할 SNS채널과, 광고 콘텐츠 형식을 선택해주세요."
        actionLabel="선택하기"
        onAction={() => { if (!serviceType || !productFilled) return setAlertMessage('1~2단계를 먼저 완료해주세요.'); setOpenRecruitModal(true); }}
        filled={recruitFilled}
        valueText={
          recruitFilled
            ? `${['','글/사진','동영상','자율'][['','post','video','free'].indexOf(recruit.contentType)] || ''} · ${recruit.platforms.map(k=>PLATFORMS.find(p=>p.key===k)?.label||k).join(', ')}`
            : ''
        }
      />

      <StepBox
        title="5. 보상 포인트(1인당)와 모집 인원을 입력해주세요."
        actionLabel="입력하기"
        onAction={() => { if (!serviceType || !productFilled || !recruitFilled) return setAlertMessage('1~4단계를 먼저 완료해주세요.'); setOpenRewardModal(true); }}
        filled={rewardFilled}
        valueText={rewardFilled ? `리워드: ${Number(reward.rewardPointPerCreator).toLocaleString()}pt · 인원: ${reward.headcount}명` : ''}
      />

      <StepBox
        title="6. CPS 리워드 제공 여부를 설정해주세요. (인플루언서가 유입한 구매 고객 수에 따라 성과형으로 지급하는 리워드입니다.)"
        actionLabel="설정하기"
        onAction={() => { if (!serviceType || !productFilled || !recruitFilled || !rewardFilled) return setAlertMessage('1~5단계를 먼저 완료해주세요.'); setOpenPerRewardModal(true); }}
        filled={perRewardFilled}
        valueText={
          perReward.use === null || typeof perReward.use === 'undefined'
            ? ''
            : (perReward.use
                ? `사용: 예, 1인당 ${Number(perReward.rewardPerBuyer||0).toLocaleString()}pt · 예산 ${Number(perReward.budgetTotal||0).toLocaleString()} · 결산 ${Number(perReward.settlementCount||0)}회`
                : '사용 안함')
        }
      />

      {/* 섹션 3: 상세 정보 입력 */}
      <div className="stepgroup">상세 정보 입력</div>
      <StepBox
        title="7. 프로젝트 일정을 설정해주세요."
        actionLabel="선택하기"
        onAction={() => {
          if (!serviceType || !productFilled || !recruitFilled || !rewardFilled) return setAlertMessage('1~5단계를 먼저 완료해주세요.');
          setOpenScheduleModal(true);
        }}
        filled={scheduleFilled}
        valueText={
          scheduleFilled
            ? `모집마감: ${fmt(schedule.recruitEnd)} · 제출마감: ${fmt(schedule.submitEnd)}${perReward.use && Number(perReward.settlementCount||0)>0 ? ` · 결산일 ${schedule.settlementDates.length}개` : ''}`
            : ''
        }
      />

      <StepBox
        title="8. 프로젝트 설명 및 요청사항을 입력해주세요."
        actionLabel="입력하기"
        onAction={() => { if (!serviceType || !productFilled || !recruitFilled || !rewardFilled || !scheduleFilled) return setAlertMessage('1~7단계를 먼저 완료해주세요.'); setOpenDescModal(true); }}
        filled={descFilled}
        valueText={descFilled ? `입력됨 (${plainLen(description)}자)` : ''}
      />

      <StepBox
        title="9. 광고 프로젝트 담당자 정보를 입력해주세요.(선택)"
        actionLabel="입력하기"
        onAction={() => { if (!serviceType || !productFilled || !recruitFilled || !rewardFilled || !scheduleFilled || !descFilled) return setAlertMessage('1~8단계를 먼저 완료해주세요.'); setOpenManagerModal(true); }}
        filled={managerHasAny}
        valueText={managerHasAny ? `${manager.phone || ''} · ${manager.email || ''} · ${manager.officeHour || ''}` : ''}
      />

      {!canProceed && (
        <div className="note note--amber" style={{ margin: '16px 0' }}>
          8단계 이상 완료해야 프로젝트를 생성할 수 있습니다.
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: canProceed ? 16 : 0 }}>
        <button
          type="button"
          onClick={() => setOpenPreview(true)}
          disabled={!canProceed}
          className="mb-btn mb-btn-secondary mb-btn-lg"
          style={{ flex: 1 }}
        >
          미리보기
        </button>
        <button
          type="button"
          onClick={() => setOpenContract(true)}
          disabled={!canProceed}
          className="mb-btn mb-btn-primary mb-btn-lg"
          style={{ flex: 2 }}
        >
          프로젝트 생성하기
        </button>
      </div>

      <ServiceTypeModal open={openServiceModal} onClose={() => setOpenServiceModal(false)} projectId={projectId} onAfterChange={({ projectId: pid, serviceType: st, resetAll }) => { if (pid) setProjectId(pid); if (st) setServiceType(st); if (resetAll) { resetAllAfterServiceChange(); } }} />
      <ProductModal open={openProductModal} onClose={() => setOpenProductModal(false)} projectId={projectId} serviceType={serviceType} initialProduct={product} onSaved={setProduct} />
      <ImageModal open={openImageModal} onClose={() => setOpenImageModal(false)} initialCover={cover} initialGallery={gallery} onSaved={(c, g) => { setCover(c); setGallery(g); }} />
      <RecruitModal open={openRecruitModal} onClose={() => setOpenRecruitModal(false)} projectId={projectId} initialRecruit={recruit} onSaved={setRecruit} />
      <RewardModal open={openRewardModal} onClose={() => setOpenRewardModal(false)} projectId={projectId} initialReward={reward} onSaved={(r) => {
        const prevHeadcount = Number(reward.headcount) || 0;
        const newHeadcount = Number(r.headcount) || 0;
        setReward(r);
        
        // ✅ 모집인원이 변경되었고, 구매당 리워드가 이미 설정된 경우 초기화
        if (prevHeadcount !== newHeadcount && perReward.use === true) {
          setPerReward({ use: null, rewardPerBuyer: '', budgetTotal: '', settlementCount: '' });
          setSchedule({ recruitEnd: '', submitEnd: '', settlementDates: [] });
          setAlertMessage('모집인원이 변경되었습니다. 6단계(구매당 리워드)와 7단계(일정)를 다시 설정해주세요.');
        }
      }} />
      <PerRewardModal open={openPerRewardModal} onClose={() => setOpenPerRewardModal(false)} projectId={projectId} initialPerReward={perReward} headcount={Number(reward.headcount) || 0} onSaved={(r) => {
        const prev = perReward;
        setPerReward(r);
        const wasUnset = prev.use === null || typeof prev.use === 'undefined';
        const useChanged = Boolean(prev.use) !== Boolean(r.use);
        const countChanged = Number(prev.settlementCount || 0) !== Number(r.settlementCount || 0);
        if (!wasUnset && (useChanged || (r.use && countChanged))) {
          setSchedule({ recruitEnd: '', submitEnd: '', settlementDates: [] });
          setAlertMessage('6단계 설정이 변경되었습니다. 7단계 일정 입력을 다시 진행해주세요.');
        }
      }} />
      <ScheduleModal open={openScheduleModal} onClose={() => setOpenScheduleModal(false)} projectId={projectId} perReward={perReward} initialSchedule={schedule} onSaved={setSchedule} />
      <DescriptionModal open={openDescModal} onClose={() => setOpenDescModal(false)} projectId={projectId} initialDescription={description} onSaved={setDescription} />
      <ManagerModal open={openManagerModal} onClose={() => setOpenManagerModal(false)} projectId={projectId} initialManager={manager} onSaved={setManager} />
      <ProjectPreviewModal
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        serviceType={serviceType}
        product={product}
        cover={cover}
        gallery={gallery}
        recruit={recruit}
        reward={reward}
        perReward={perReward}
        schedule={{ ...schedule, settlementDates: perReward?.use ? schedule?.settlementDates : [] }}
        description={description}
        manager={manager}
      />
      <ContractModal
        open={openContract}
        onClose={() => setOpenContract(false)}
        projectId={projectId}
        cover={cover}
        gallery={gallery}
        recruit={recruit}
        reward={reward}
        perReward={perReward}
        onStartCommit={() => { setOpenContract(false); setOpenProcessing(true); }}
        onCommitSuccess={() => { setOpenProcessing(false); setOpenDone(true); }}
        onCommitFail={(m) => { setOpenProcessing(false); setOpenContract(true); alert(m || '처리에 실패했습니다.'); }}
      />
      <ProcessingModal open={openProcessing} />
      <DoneModal
        open={openDone}
        onClose={() => {
          try { window.__leavingToPrjOn = true; } catch {}
          setOpenDone(false);
          window.location.replace('/marketer/mybiz?tab=project&sub=prjOn');
        }}
      />
      <ConfirmModal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={() => setOpenServiceModal(true)}
        title="프로젝트 방식 변경"
        message={`1단계를 변경하면 2~9단계 정보를 모두 다시 입력해야 합니다.\n수정하시겠습니까?`}
      />
      <AlertModal 
        open={!!alertMessage} 
        onClose={() => setAlertMessage('')} 
        message={alertMessage} 
      />
    </div>
  );
}
